import React from 'react';
import { IconButton, List, ListItem, ListItemText, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { MessagingToDoStatus } from '@uplift/api';
import { useMessagingToDosWithStatusQuery, useMessagingToDoQuery, useCompleteMessagingToDoMutation, useArchiveMessagingToDoMutation } from '@uplift/queries';
import { ClosableMenu, ClosableMenuItem } from '../../lib/simple-menu';
import { MoreVert } from '@mui/icons-material';
import { asyncWait, ellipsis } from '@uplift/utils';
import { withStyles } from 'tss-react/mui';


const ToDoSidebarItem: React.FC<{
  todoId: string;
  selected: boolean;
  onSelect: (todoId: string) => void;
}> = ({ todoId, selected, onSelect }) => {
  const anchorElRef = React.useRef<HTMLButtonElement | null>(null);
  const [ menuOpen, setMenuOpen ] = React.useState<boolean>(false);
  const { data: todo } = useMessagingToDoQuery(todoId, { suspense: true });
  const archiveMessagingToDoMutation = useArchiveMessagingToDoMutation();
  const completeMessagingToDoMutation = useCompleteMessagingToDoMutation();

  if (!todo) return null;

  return (
    <ListItem
      alignItems="center"
      data-testid="todo-item"
      secondaryAction={(
        <>
          <ClosableMenu
            anchorEl={anchorElRef.current}
            onClose={() => setMenuOpen(false)}
            open={menuOpen}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center'
            }}
          >
            <ClosableMenuItem
              disabled={!!todo.archivedAt}
              onClick={async () => {
                await Promise.all([
                  completeMessagingToDoMutation.mutateAsync({ todoId: todo.id, complete: !todo.completedAt }),
                  asyncWait(600),
                ]);
              }}
            >
              {todo.completedAt ? 'Uncomplete' : 'Complete'}
            </ClosableMenuItem>

            <ClosableMenuItem
              disabled={!!todo.completedAt}
              onClick={async () => {
                await Promise.all([
                  archiveMessagingToDoMutation.mutateAsync({ todoId: todo.id, archive: !todo.archivedAt }),
                  asyncWait(600),
                ]);
              }}
            >
              {todo.archivedAt ? 'Unarchive' : 'Archive'}
            </ClosableMenuItem>
          </ClosableMenu>
          <IconButton ref={anchorElRef} onClick={() => setMenuOpen(true)}>
            <MoreVert />
          </IconButton>
        </>
      )}
      sx={{
        ...(selected ? {
          backgroundColor: 'grey.100',
          '&:hover': {
            backgroundColor: 'grey.100',
          }
        } : {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'grey.50',
          },
        }),
      }}
    >
      <ListItemText
        onClick={() => onSelect(todo.id)}
        sx={{ maxHeight: '3em', overflow: 'hidden' }}
        primary={ellipsis(todo.description, 36)}
        secondary={ellipsis(todo.message.message, 50)}
      />
    </ListItem>
  );
};


const ToDosStatusToggleButton = withStyles(ToggleButton, {
  root: {
    border: 'none',
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: 450,
    '&.Mui-selected': {
      fontWeight: 500,
      backgroundColor: 'transparent',
    },
  }
});


export const MessagingToDosFilterMenu: React.FC<{ value: MessagingToDoStatus; onChange: (value: MessagingToDoStatus) => void }> = ({ value, onChange }) => {
  return (
    <ToggleButtonGroup
      size="small"
      fullWidth
      value={value}
      exclusive
      onChange={(_, v) => {
        v && onChange(v as MessagingToDoStatus);
      }}
    >
      <ToDosStatusToggleButton value="active">To Do</ToDosStatusToggleButton>
      <ToDosStatusToggleButton value="completed">Completed</ToDosStatusToggleButton>
      <ToDosStatusToggleButton value="archived">Archived</ToDosStatusToggleButton>
    </ToggleButtonGroup>
  );
};


export const MessagingToDosList: React.FC<{
  status: MessagingToDoStatus;
  selectedToDoId?: string;
  setSelectedToDoId: (selectedToDoId: string) => void;
}> = ({
  status,
  selectedToDoId,
  setSelectedToDoId
}) => {
  const { data: todos = [] } = useMessagingToDosWithStatusQuery(status ?? 'active', { suspense: true });

  if (todos.length === 0) {
    return (
      <Typography sx={{ pt: 3 }} align="center" color="textSecondary" height="100%" width="100%">No to dos found.</Typography>
    );
  }

  return (
    <List sx={{ width: '100%', minHeight: '100%', boxSizing: 'border-box' }}>
      {todos.map(todoId => (
        <ToDoSidebarItem
          key={todoId}
          todoId={todoId}
          selected={selectedToDoId === todoId}
          onSelect={setSelectedToDoId}
        />
      ))}
    </List>
  );
};
